import React, { FormEvent, useEffect, useState } from "react";
import { Header, Modal, StoreLocator } from "dstill";
import { useContentful } from "../../utils/hooks/contentful/useContentful";
import { usePosition } from "../../utils/hooks/usePosition";
import { useLocalStorage } from "../../utils/hooks/useLocalStorage";
import { Store } from "../../types/StoreLocator";
import { getStoresByAddress, getStoresByCoordinates } from "../../utils/api";
import algoliasearch from "algoliasearch";
import { filterUniqueStores, getLinkHref } from "../../utils/helpers";
import { track, trackItem } from "../../utils/hooks/useSegment";
import {
	TypeHeaderFields,
	TypeStoreLocatorFields
} from "../../types/contentful";
import { useLocale } from "../../utils/hooks/locale/useLocale";
import {
	getBrandLinks,
	getBrandLogo,
	getHeaderLinks,
	getLinks,
	getHeaderLinksJsonLd,
	getBrandLinksJsonLd,
	getLinksJsonLd,
	getBrandLogoJsonLd,
	getProductResults,
	getStoreContent
} from "../../utils/hooks/contentful/contentParsers/header.parser";
import { t } from "i18next";
import useSize from "../../utils/hooks/useSize";
import { useEnv } from "../../utils/hooks/useEnv";

type Result = {
	id: number;
	title: string;
	handle: string;
	product_url: string;
	product_image: string;
	product_type: string;
	vendor: string;
	image: string;
	variants_min_price: number;
	variants_max_price: number;
	variant_title: string;
	price: number;
	compare_at_price: number;
	sku: string;
};

export const IframeHeader = () => {
	const target = React.useRef(null);
	const size = useSize(target);
	const searchParams = new URLSearchParams(location.search);
	const [jsonLd, setJsonLd] = useState<any>([]);
	const {
		algoliaAppId,
		algoliaSearchAPIKey,
		algoliaIndexPrefix,
		storeLocatorEntryId,
		headerEntryId
	} = useEnv();

	const client = algoliasearch(algoliaAppId || "", algoliaSearchAPIKey || "");

	// Create a new index and add a record
	const index = client.initIndex(algoliaIndexPrefix + "products");

	const { locale, setLocale, localeCase } = useLocale();
	const lang = searchParams.get("lng");
	if (lang) {
		setLocale(lang);
	}

	const { getEntry } = useContentful();

	const { latitude, longitude } = usePosition();
	const [lat, setLat] = useLocalStorage("lat", latitude.toString());
	const [lng, setLng] = useLocalStorage("lng", longitude.toString());

	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingSearch, setIsLoadingSearch] = useState(false);
	const [searchText, setSearchText] = useState<string>("");
	const [currentStore, setCurrentStore] = useState<Store | null>(null);
	const [remainingStores, setRemainingStores] = useState<Store[]>([]);
	const [totalSearchHits, setTotalSearchHits] = useState<number>(0);
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [searchProductsList, setSearchProductsList] = useState<any>([]);

	const [storeLocatorContentfulFields, setStoreLocatorContentfulFields] =
		useState<any>({
			// TODO - add type here via a distill export
			title: "",
			closeButtonAltText: "",
			myStoreTitle: "",
			directionsLink: {
				title: "",
				url: ""
			},
			storeDetailsLink: {
				title: ""
			},
			hoursOfOperationTitle: "",
			monday: "",
			tuesday: "",
			wednesday: "",
			thursday: "",
			friday: "",
			saturday: "",
			sunday: "",
			storeSearchTitle: "",
			searchInputPlaceHolderText: "",
			searchButtonText: "",
			nearbyStoresText: "",
			makeMyStoreLinkText: "",
			storeFinderButtonLink: {
				title: "",
				url: ""
			},
			searchStoreErrorTitle: "",
			searchStoreErrorBodyText: "",
			searchStoreErrorTryAgain: ""
		});

	const [headerContentfulFields, setHeaderContentfulFields] = useState<any>({
		brandLinks: [],
		links: [],
		brandBarLogo: {
			imageUrl: "",
			href: "",
			altText: "",
			target: ""
		},
		headerLinks: [],
		backButtonText: "",
		searchPlaceholderText: "",
		searchResultsMatchText: "",
		searchNoResultsText: ""
	});

	const [events, setEvents] = useState<any[]>([]);

	window.addEventListener("message", (event) => {
		if (event.data.type !== "CHANGE_NAV_LINK_HEADER") return;
		setEvents((prevState) => [...prevState, event]);
	});
	const setupParentUrl = () => {
		const urlParams = new URLSearchParams(window.location.search);
		const rawParentUrl = urlParams.get("parentUrl");
		const parentUrl = rawParentUrl ? decodeURIComponent(rawParentUrl) : null;
		(window as any).iframeConfig = { parentUrl: parentUrl };
	};
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", setupParentUrl);
	} else {
		setupParentUrl();
	}
	useEffect(() => {
		events.forEach((event) => {
			const link = document.getElementById(
				"header-" + event.data.linkId
			) as HTMLAnchorElement;
			if (link) {
				link.href = event.data.href;
			}

			const mobileLink = document.getElementById(
				"header-" + event.data.linkId + "-mobile"
			) as HTMLAnchorElement;
			if (mobileLink) {
				mobileLink.href = event.data.href;
			}
		});
	});

	/**
	 * Header in Mediaclip requires special handling for scroll
	 * behavior because it is embedded in an overlay iframe.
	 */
	const handleMCMenu = () => {
		const vendor = searchParams.get("vendor") || undefined;
		const isMediaclip = vendor === "mediaclip";

		const toggleBodyOverflow = (isOpen: boolean) => {
			if (isMediaclip) {
				document.body.style.overflow = isOpen ? "auto" : "hidden";
			}
		};

		return {
			open: () => toggleBodyOverflow(true),
			close: () => toggleBodyOverflow(false)
		};
	};

	const [isOpen, setIsOpen] = useState(false);
	const handleOpen = (event?: React.SyntheticEvent) => {
		event?.preventDefault();
		window.parent.postMessage("STAPLES_IFRAME_STORE_LOCATOR_OPEN", "*");

		setIsOpen(true);
	};
	const handleClose = () => {
		window.parent.postMessage("STAPLES_IFRAME_STORE_LOCATOR_CLOSE", "*");

		setIsOpen(false);
	};
	const handleStoreChanged = () => {
		window.parent.postMessage(
			{
				event: "STAPLES_IFRAME_STORE_CHANGED",
				address: currentStore?.address_1
			},
			"*"
		);
	};
	const handleResultShown = () => {
		window.parent.postMessage("STAPLES_IFRAME_SEARCH_RESULT_OPEN", "*");
	};

	const handleResultHidden = () => {
		window.parent.postMessage("STAPLES_IFRAME_SEARCH_RESULT_CLOSE", "*");
	};
	const handleMenuShown = () => {
		handleMCMenu().open();
		if (window.innerWidth < 1024) {
			document.body.style.overflow = "hidden";
		}
		window.parent.postMessage("STAPLES_IFRAME_MENU_OPEN", "*");
		if (window.innerWidth < 1024) {
			setTimeout(() => {
				document.body.style.overflow = "auto";
			}, 20);
		}
	};

	const handleMenuHidden = () => {
		handleMCMenu().close();
		window.parent.postMessage("STAPLES_IFRAME_MENU_CLOSE", "*");
	};
	const searchResultClicked = (item: any) => {
		item.product_name = item.name;
		item.copy = t("search.cta");
		trackItem(item, "search results");
	};
	useEffect(() => {
		handleStoreChanged();
	}, [currentStore]);
	useEffect(() => {
		window.parent.postMessage(
			{
				event: "STAPLES_IFRAME_HEADER_SIZE_CHANGE",
				width: size?.width,
				height: size?.height
			},
			"*"
		);
	}, [size]);
	useEffect(() => {
		(async () => {
			const stores = await getStoresByCoordinates(lat, lng);
			const vendor = searchParams.get("vendor") || undefined;

			// My Store - left hand column
			const uniqueStores = filterUniqueStores(stores);
			setCurrentStore(uniqueStores[0]);

			// Store Search - right hand column
			const remainingStores = uniqueStores.slice(1, uniqueStores.length);
			setRemainingStores(remainingStores);
			const parsedLocale = locale === "en-CA" ? "en-US" : locale;

			if (storeLocatorEntryId) {
				const contentfulStoreLocator = await getEntry<TypeStoreLocatorFields>(
					storeLocatorEntryId,
					parsedLocale
				);

				setStoreLocatorContentfulFields({
					...contentfulStoreLocator.fields,
					...getStoreContent(contentfulStoreLocator, vendor)
				});
			}

			if (headerEntryId) {
				const contentfulHeader = await getEntry<TypeHeaderFields>(
					headerEntryId,
					parsedLocale
				);

				setJsonLd([]);

				const addToJsonLd = (newSchemas: any[]) =>
					setJsonLd((prevState: any[]) => [...prevState, ...newSchemas]);

				const brandLinks = getBrandLinks(
					contentfulHeader.fields.brandLinks || [],
					vendor
				);

				addToJsonLd(getBrandLinksJsonLd(brandLinks));

				const links = getLinks(
					contentfulHeader.fields.utilityLinks || [],
					localeCase,
					vendor
				);

				addToJsonLd(getLinksJsonLd(links));

				const brandBarLogo = getBrandLogo(
					contentfulHeader.fields.brandLogo?.fields,
					vendor
				);

				setJsonLd((prevState: any[]) => [
					...prevState,
					getBrandLogoJsonLd(brandBarLogo)
				]);

				const headerLinks = getHeaderLinks(
					contentfulHeader.fields.navigation || [],
					localeCase,
					vendor
				);

				addToJsonLd(getHeaderLinksJsonLd(headerLinks));

				setHeaderContentfulFields({
					...contentfulHeader.fields,
					brandLinks,
					links,
					brandBarLogo,
					headerLinks
				});
			}

			setIsLoading(false);
		})();
	}, [headerEntryId, storeLocatorEntryId, locale]);

	const storeLocatorProps = {
		isLoading,
		isLoadingSearch,
		locale,
		currentStore,
		searchedStores: remainingStores,
		searchText,
		onLinkClicked: (evt: React.SyntheticEvent) => {
			const target = evt.nativeEvent.target as HTMLSpanElement;
			const targetAnchor = target?.parentElement
				?.parentElement as HTMLAnchorElement;
			const obj = {
				link_name: target?.innerText,
				destination_url: targetAnchor?.href,
				link_location: "header",
				external: targetAnchor?.target === "_blank"
			};
			track(obj);
		},
		onChangeSearchText: (event: React.FormEvent<HTMLInputElement>) => {
			const value = (event.target as HTMLInputElement).value;
			setSearchText(value);
		},
		onSubmitSearch: async (event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();

			setIsLoadingSearch(true);

			const stores = await getStoresByAddress(searchText);

			const uniqueStores = filterUniqueStores(stores);

			setRemainingStores(uniqueStores.slice(0, uniqueStores.length));

			setIsLoadingSearch(false);
			const trackObj = {
				title: storeLocatorContentfulFields.searchButtonText,
				destination_url: "",
				target: ""
			};
			trackItem(trackObj, "store locator");
		},
		onClickMakeMyStore: async (lat: string, lng: string, evt: any) => {
			evt.preventDefault();
			setIsLoading(true);

			const stores = await getStoresByCoordinates(lat, lng);

			setLat(lat);
			setLng(lng);

			// My Store - left hand column
			const uniqueStores = filterUniqueStores(stores);
			setCurrentStore(uniqueStores[0]);

			// Store Search - right hand column
			const remainingStores = uniqueStores.slice(1, uniqueStores.length);
			setRemainingStores(remainingStores);

			setIsLoading(false);
			const trackObj = {
				title: storeLocatorContentfulFields.makeMyStoreLinkText,
				destination_url: "",
				target: ""
			};
			trackItem(trackObj, "store locator");
		},
		onClickStoreLocator: (item: any) => {
			item.title = item.linkBodyText;
			trackItem(item, "store locator");
			return true;
		},
		contentful: storeLocatorContentfulFields
	};

	const getProducts = async (keyword: string) => {
		const vendor = searchParams.get("vendor") || undefined;
		const search = await index.search<Result>(keyword, {
			distinct: true,
			filters: locale === "fr-CA" ? "tags:fr-CA" : "tags:en-CA"
		});

		setTotalSearchHits(search.nbHits);
		setSearchQuery(keyword);
		const localePart = locale === "fr-CA" ? "fr/" : "";
		const products = search.hits.map(
			(
				{
					id,
					title,
					variants_min_price,
					product_image,
					product_type,
					vendor: brand,
					handle,
					product_url,
					sku,
					variant_title,
					compare_at_price,
					variants_max_price
				},
				productIndex
			) =>
				getProductResults(
					{
						title,
						product_image,
						variants_min_price,
						product_url,
						cta:
							product_type === "Services"
								? t("search.serviceCta")
								: t("search.cta"),
						sku,
						variant_title,
						compare_at_price,
						id,
						variants_max_price,
						localePart,
						handle,
						productIndex,
						query: search.query,
						brand
					},
					vendor
				)
		);
		const searchProducts = search.hits.map(
			(
				{
					id,
					title,
					variants_min_price,
					vendor: brand,
					handle,
					sku,
					variant_title
				},
				productIndex
			) => ({
				name: title,
				brand,
				category: `${localePart}products/${handle}`,
				position: productIndex + 1,
				price: variants_min_price,
				product_id: id ? id.toString() : "",
				sku: sku,
				variant: variant_title
			})
		);
		setSearchProductsList(searchProducts);

		const Updatedproducts = products.map((product) => ({
			...product,
			productList: searchProducts
		}));
		return Updatedproducts;
	};
	const headerItemClicked = (item: any) => {
		trackItem(item, "header");
	};
	const dropdownHeightChanged = (height?: number) => {
		window.parent.postMessage(
			{
				event: "STAPLES_IFRAME_HEADER_DROPDOWN_HEIGHT_CHANGE",
				height: height
			},
			"*"
		);
	};

	const vendor = searchParams.get("vendor") || undefined;
	const searchUrl = getLinkHref(
		process.env.REACT_APP_SHOPIFY_STORE_DOMAIN +
			(locale === "fr-CA" ? "fr/search?q=" : "search?q="),
		vendor
	);

	window.addEventListener("message", (event) => {
		const data = event.data;
		if (data && data === "STAPLES_IFRAME_OPEN_STORE_LOCATOR") {
			handleOpen();
		} else if (data && data === "STAPLES_IFRAME_REQUEST_CURRENT_STORE") {
			handleStoreChanged();
		}
	});

	return (
		<div>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
			></script>
			<Header
				ref={target}
				brands={headerContentfulFields.brandLinks}
				links={[
					{
						href: "#address",
						children: currentStore?.address_1,
						iconName: "location",
						onClick: handleOpen
					},
					...headerContentfulFields.links
				]}
				brandBarLogo={headerContentfulFields.brandBarLogo}
				searchAllResultClicked={(evt: React.SyntheticEvent) => {
					const target = evt.nativeEvent.target as HTMLSpanElement;
					const obj = {
						copy: target?.innerText,
						url:
							totalSearchHits > 0
								? process.env.REACT_APP_SHOPIFY_STORE_DOMAIN +
								  (locale === "fr-CA" ? "fr/search?q=" : "search?q=") +
								  target?.innerText
								: searchUrl,
						productList: searchProductsList,
						query: searchQuery,
						isProductSearch: true
					};
					trackItem(obj, "search results");
				}}
				headerLinks={headerContentfulFields.headerLinks}
				search={{
					placeholder: headerContentfulFields.searchPlaceholderText,
					searchResultsMatchText: headerContentfulFields.searchMatchText || "",
					searchNoResultsText: headerContentfulFields.searchNoResultsText || "",
					allProductsLink: searchUrl || "",
					algoliaSearchUrl: searchUrl || "",
					getProducts,
					totalSearchHits,
					resultsShown: handleResultShown,
					resultsHidden: handleResultHidden,
					searchResultClicked: searchResultClicked
				}}
				backButtonTitle={headerContentfulFields.backButtonText}
				isSideMenuOpen={false}
				hideUtilityBar={false}
				setIsSideMenuOpen={() => alert("menu opened")}
				menuOpen={handleMenuShown}
				menuClose={handleMenuHidden}
				itemClicked={headerItemClicked}
				dropdownHeightChanged={dropdownHeightChanged}
			/>
			<Modal
				closeButtonAltText={storeLocatorContentfulFields.closeButtonAltText}
				modalId={storeLocatorContentfulFields.title}
				isOpen={isOpen}
				handleClose={handleClose}
			>
				<StoreLocator {...storeLocatorProps} />
			</Modal>
		</div>
	);
};
